// Baselayer Results Explorer — Reviews & Socials tab. Reads the SAME
// online-presence add-on as the Website tab (ordered as either WebsiteAnalysis
// or ReviewsSocials) and renders two columns: Reviews | Socials. All fields
// UNVERIFIED → parsed defensively.

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/design-system/components/ui";
import type { BackgroundCheckAddOnDto } from "@/api/backgroundChecks/addOnsApi";
import { AddOnEmptyState } from "../AddOnEmptyState";
import { Chip } from "../tabHelpers";
import { findAddOn, isCompleted } from "../tabHelpers";
import {
  asArray,
  asObject,
  asNumber,
  asString,
  pick,
  safeParse,
} from "../parse";

interface ReviewsSocialsTabProps {
  checkId: string;
  addOns: BackgroundCheckAddOnDto[] | undefined;
}

export function ReviewsSocialsTab({ checkId, addOns }: ReviewsSocialsTabProps) {
  const addOn = findAddOn(addOns, "ReviewsSocials", "WebsiteAnalysis");

  if (!addOn || !isCompleted(addOn)) {
    return (
      <AddOnEmptyState
        checkId={checkId}
        addOnType="ReviewsSocials"
        label="Reviews & Socials"
        title="No reviews & socials ordered"
        description="Order reviews & socials to see review ratings and social profiles for this subject."
        addOn={addOn}
      />
    );
  }

  const root = asObject(safeParse(addOn.rawResponseJson));
  const web =
    asObject(pick(root, "online_presence", "web_presence", "website")) ?? root;

  const reviews = asArray(pick(web, "reviews", "review_sources")).map((r) => {
    const o = asObject(r);
    return {
      source: asString(pick(o, "source", "site", "name")) ?? "Reviews",
      rating: asNumber(pick(o, "rating", "score", "stars")),
      count: asNumber(pick(o, "count", "review_count")),
    };
  });

  const socials = asArray(
    pick(web, "social_profiles", "social_profiles_match", "socials"),
  ).map((s) => {
    const o = asObject(s);
    return {
      site: asString(pick(o, "site", "platform", "network")) ?? "Profile",
      value: asString(pick(o, "value", "url", "handle")),
    };
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-space-4">
      <Card>
        <CardHeader>
          <CardTitle>Reviews</CardTitle>
        </CardHeader>
        <CardContent>
          {reviews.length === 0 ? (
            <p className="text-small text-gray-mid m-0">No reviews found.</p>
          ) : (
            <ul className="m-0 list-none flex flex-col gap-space-2">
              {reviews.map((r, i) => (
                <li
                  key={`${r.source}-${i}`}
                  className="flex items-center justify-between text-small text-onyx"
                >
                  <span>{r.source}</span>
                  <span className="text-gray-mid">
                    {r.rating != null ? `${r.rating}★` : "—"}
                    {r.count != null ? ` (${r.count})` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Socials</CardTitle>
        </CardHeader>
        <CardContent>
          {socials.length === 0 ? (
            <p className="text-small text-gray-mid m-0">
              No social profiles found.
            </p>
          ) : (
            <ul className="m-0 list-none flex flex-col gap-space-2">
              {socials.map((s, i) => (
                <li
                  key={`${s.site}-${i}`}
                  className="flex items-center justify-between gap-space-2 text-small text-onyx"
                >
                  <Chip>{s.site}</Chip>
                  {s.value ? (
                    s.value.startsWith("http") ? (
                      <a
                        href={s.value}
                        target="_blank"
                        rel="noreferrer"
                        className="text-gold-shadow hover:underline break-all"
                      >
                        {s.value}
                      </a>
                    ) : (
                      <span className="break-all">{s.value}</span>
                    )
                  ) : (
                    <span className="text-gray-mid">—</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
